import { useState, useCallback, useMemo } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { type Personnel } from '@/features/personnel/types';
import { type ToolConfig, type WorkspaceConfig } from '@/features/layout/workspace-config';
import {
    buildCommandList,
    fuzzySearch,
    type Command,
    type PersonnelRecord,
} from './command-registry';

interface UseCommandPaletteOptions {
    /** Called to switch to a workspace */
    onSwitchWorkspace: (id: string) => void;
    /** Called to switch to a tool */
    onSwitchTool: (id: string) => void;
    /** Workspace commands visible to the current user. Defaults to all workspaces. */
    workspaces?: WorkspaceConfig[];
    /** Tool commands visible to the current user. Defaults to all tools. */
    tools?: ToolConfig[];
}

export interface UseCommandPaletteReturn {
    isOpen: boolean;
    query: string;
    selectedIndex: number;
    results: Command[];
    open: () => void;
    close: () => void;
    toggle: () => void;
    setQuery: (query: string) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    executeSelected: () => void;
}

/**
 * Hook managing Command Palette state: open/close, search, keyboard navigation.
 * Reads personnel from the Zustand store so the palette can search team members.
 */
export function useCommandPalette({
    onSwitchWorkspace,
    onSwitchTool,
    workspaces,
    tools,
}: UseCommandPaletteOptions): UseCommandPaletteReturn {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Personnel from store
    const personnel = useAppStore(state => state.personnel);

    // Build personnel records for command registry
    const personnelRecords: PersonnelRecord[] = useMemo(() => {
        if (!personnel || !Array.isArray(personnel)) return [];
        return personnel
            .filter((p: Personnel) => !p.deleted)
            .map((p: Personnel) => ({
                id: p.id,
                name: p.name || 'Unknown',
                role: p.primaryRole || p.title || '',
            }));
    }, [personnel]);

    // Build all commands
    const allCommands = useMemo(() => {
        return buildCommandList(
            onSwitchWorkspace,
            onSwitchTool,
            personnelRecords,
            (_id) => {
                // Navigate to personnel view with this person selected
                onSwitchTool('cast');
                // Could also set a selected personnel ID in the store
            },
            { workspaces, tools },
        );
    }, [onSwitchWorkspace, onSwitchTool, personnelRecords, workspaces, tools]);

    // Fuzzy search results
    const results = useMemo(() => fuzzySearch(allCommands, query), [allCommands, query]);

    // Reset selection when query changes
    const [prevQuery, setPrevQuery] = useState(query);
    if (query !== prevQuery) {
        setSelectedIndex(0);
        setPrevQuery(query);
    }

    const open = useCallback(() => {
        setIsOpen(true);
        setQuery('');
        setSelectedIndex(0);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
    }, []);

    const toggle = useCallback(() => {
        if (isOpen) close();
        else open();
    }, [isOpen, open, close]);

    const executeSelected = useCallback(() => {
        if (results.length > 0 && selectedIndex < results.length) {
            results[selectedIndex]!.action();
            close();
        }
    }, [results, selectedIndex, close]);

    const onKeyDown = useCallback((e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                executeSelected();
                break;
            case 'Escape':
                e.preventDefault();
                close();
                break;
        }
    }, [results.length, executeSelected, close]);

    return {
        isOpen,
        query,
        selectedIndex,
        results,
        open,
        close,
        toggle,
        setQuery,
        onKeyDown,
        executeSelected,
    };
}
